// ─── Route access decision ────────────────────────────────
//
// The pure half of <RequireRole>. Extracted so the bootstrap window can be
// reasoned about — and tested — without a router, a store or a DOM.
//
// The defect this exists to prevent: a red "You do not have permission to
// access this page." toast on every normal login. It was never an
// authorization failure the user caused. Two things combined:
//
//   1. a bootstrap redirect sent a role to a route it cannot read
//      (see FileExchangeScreen — fixed by deriving the landing page from
//      getLandingPath, the same resolver the sidebar and the login
//      redirect use), and
//   2. this guard announced the denial the instant a role could be
//      guessed, including from the localStorage hydration cache, before
//      the authoritative session had resolved.
//
// (2) is the general rule and lives here: an identity recovered from
// localStorage is good enough to decide where to send someone — that
// avoids a flash of content on hard refresh — but it is NOT good enough
// to tell them they did something they are not allowed to do. Only the
// resolved session speaks.
//
// The redirect still happens either way, so a genuinely unauthorized URL
// never renders. All that is withheld during bootstrap is the accusation.

import { hasPermission } from '../config/rbac';
import { isPortalUser } from './rbac';

export const ACCESS = {
  /** Session is being fetched and nothing is known yet — hold. */
  WAIT:   'wait',
  /** No identity at all — send to /login. */
  LOGIN:  'login',
  /** A portal role on a back-office route — send to their portal home. */
  PORTAL: 'portal',
  /** Known identity, insufficient permission — bounce. */
  DENY:   'deny',
  /** Render the children. */
  ALLOW:  'allow',
};

/**
 * @param {object}  input
 * @param {?string} input.reduxRole      role from the resolved session (authoritative)
 * @param {?string} input.cachedRole     role from localStorage (hydration cache only)
 * @param {boolean} input.sessionLoading a /users/get-user request is in flight
 * @param {string}  input.resource       RBAC resource being guarded
 * @param {string}  input.action         RBAC action being guarded
 * @returns {{ decision: string, role: ?string, allowed: boolean, toast: boolean }}
 */
export function resolveRouteAccess({
  reduxRole = null,
  cachedRole = null,
  sessionLoading = false,
  resource,
  action,
} = {}) {
  const role = reduxRole || cachedRole || null;

  // Nothing known. If the session fetch is still running this is the
  // bootstrap window, not a logged-out user — holding beats bouncing
  // someone with a valid cookie out to /login.
  if (!role) {
    return {
      decision: sessionLoading ? ACCESS.WAIT : ACCESS.LOGIN,
      role: null,
      allowed: false,
      toast: false,
    };
  }

  if (isPortalUser(role)) {
    return { decision: ACCESS.PORTAL, role, allowed: false, toast: false };
  }

  const allowed = hasPermission(role, resource, action);
  if (allowed) {
    return { decision: ACCESS.ALLOW, role, allowed: true, toast: false };
  }

  // Denied. Announce it only when the identity came from the resolved
  // session — a cached role is a hydration artifact, and telling a user
  // mid-bootstrap that they lack permission is a lie roughly as often as
  // it is the truth.
  return {
    decision: ACCESS.DENY,
    role,
    allowed: false,
    toast: Boolean(reduxRole),
  };
}
